'use client';

import React from 'react';
import styles from './RefundPolicy.module.css';

interface RefundPolicyProps {
  enableVideo?: boolean;
  backgroundVideoMp4?: string;
  backgroundVideoWebm?: string;
  videoPoster?: string;
}

export default function RefundPolicy({
  enableVideo = true,
  backgroundVideoMp4 = '/video/rental_fog.mp4',
  backgroundVideoWebm = '/video/fog-creeping.webm',
  videoPoster = '/img/fog_rental.png',
}: RefundPolicyProps) {
  return (
    <div className={styles.container}>
      {/* Background Video */}
      {enableVideo && (
        <div className={styles.background}>
          <video
            className={styles.video}
            autoPlay
            muted
            loop
            playsInline
            poster={videoPoster}
          >
            <source src={backgroundVideoMp4} type="video/mp4" />
            <source src={backgroundVideoWebm} type="video/webm" />
          </video>
          <div className={styles.overlay} />
        </div>
      )}

      <div className={styles.wrapper}>
        <div className={styles.header}>
          <img
            loading="lazy"
            src="https://cdn.builder.io/api/v1/image/assets/TEMP/03b529fd8f1563ec8c5630d4ee62b758b4f401a3b4ef42fa7d4e3500fcf4fab6?apiKey=7d740121674b4225a077dc91176b5085&"
            className={styles.logo}
            alt="NeutraFog logo"
          />
          <div className={styles.badge}>Policy</div>
          <h1 className={styles.title}>Refund & Warranty Policy</h1>
          <p className={styles.subtitle}>
            Effective January 1, 2026
          </p>
        </div>

        <div className={styles.content}>
          {/* Intro */}
          <div className={styles.sectionContent} style={{ marginBottom: '2rem' }}>
            We stand behind our equipment — and we stand with our customers. NeutraFog systems are professional-grade tools designed to perform in real grow environments. If something isn't working as expected, we don't disappear. We help you dial it in.
          </div>

          {/* 30-Day Performance Promise */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>30-Day Performance Promise (Devices Only)</h2>
            <div className={styles.sectionContent}>
              All NeutraFog devices are covered by our 30-Day Performance Promise.
            </div>
            <div className={styles.sectionContent}>
              If your device doesn't perform as intended:
            </div>
            <ul className={styles.list}>
              <li className={styles.listItem}>Our team will work with you to optimize setup and operation.</li>
              <li className={styles.listItem}>If performance issues persist after support review, we'll make it right — including a refund or replacement where appropriate.</li>
            </ul>
            <div className={styles.highlight}>
              <strong>Note:</strong> This promise applies to devices only. Consumable solutions are excluded once used.
            </div>
          </section>

          {/* Solutions & Consumables */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Solutions & Consumables</h2>
            <div className={styles.sectionContent}>
              NeutraFog solutions are consumable products and cannot be returned once opened or used.
            </div>
            <div className={styles.sectionContent}>
              That said — if results aren't what you expected, we don't leave you hanging. Our team will:
            </div>
            <ul className={styles.list}>
              <li className={styles.listItem}>Review your application</li>
              <li className={styles.listItem}>Help adjust dosage, timing, or setup</li>
              <li className={styles.listItem}>Ensure you're using the correct solution for your environment</li>
            </ul>
            <div className={styles.sectionContent}>
              Performance issues tied to product use are handled case-by-case, with support as the first line of resolution.
            </div>
          </section>

          {/* Warranty & Manufacturer Defects */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Warranty & Manufacturer Defects</h2>
            <div className={styles.sectionContent}>
              All devices are covered against verified manufacturer defects.
            </div>
            <div className={styles.sectionContent}>
              If you believe your unit has a defect:
            </div>
            <ul className={styles.list}>
              <li className={styles.listItem}>
                Email{' '}
                <a href="mailto:support@neutrafog.com" className={styles.email}>
                  support@neutrafog.com
                </a>
              </li>
              <li className={styles.listItem}>Include your order number and photos/videos if possible</li>
            </ul>
            <div className={styles.sectionContent}>
              If a defect is confirmed, we'll determine the best resolution — repair, replacement, or refund — consistent with applicable law.
            </div>
          </section>

          {/* What's Not Covered */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>What's Not Covered</h2>
            <div className={styles.warning}>
              <ul className={styles.list}>
                <li className={styles.listItem}>Change of mind or buyer's remorse</li>
                <li className={styles.listItem}>Incorrect device selection for your application</li>
                <li className={styles.listItem}>Normal wear and tear</li>
                <li className={styles.listItem}>Damage from misuse, improper setup, or unauthorized modification</li>
              </ul>
            </div>
          </section>

          {/* Shipping Issues & Damage */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Shipping Issues & Damage</h2>
            <div className={styles.sectionContent}>
              Inspect your order upon delivery. If anything arrives damaged or incorrect, contact us immediately so we can resolve it fast.
            </div>
          </section>

          {/* Returns Prior to January 1, 2026 */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Returns Prior to January 1, 2026</h2>
            <div className={styles.sectionContent}>
              Orders placed before January 1, 2026 follow the return policy in effect at the time of purchase. Contact support and we'll confirm details.
            </div>
          </section>

          {/* Discounted Items & Restocking Fee */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Discounted Items & Restocking Fee</h2>
            <div className={styles.warning}>
              <div className={styles.sectionContent}>
                <strong>Discounted Items:</strong> All sales are final unless stated otherwise.
              </div>
            </div>
            <div className={styles.sectionContent}>
              If a return is approved for an eligible item, a 20% restocking fee will be applied.
            </div>
            <div className={styles.sectionContent}>
              Refunds will be issued to the original card on file. Please allow time for processing — typically 5-10 business days depending on your financial institution.
            </div>
          </section>

          {/* Contact Section */}
          <section className={styles.contactSection}>
            <h3 className={styles.contactTitle}>Questions or Support</h3>
            <p className={styles.contactText}>
              📩{' '}
              <a href="mailto:support@neutrafog.com" className={styles.email}>
                support@neutrafog.com
              </a>
            </p>
            <p className={styles.contactText}>
              We're here to help — before and after purchase.
            </p>
            <a
              href="mailto:support@neutrafog.com?subject=Refund%20%26%20Warranty%20Question"
              className={styles.contactButton}
            >
              Contact Support
            </a>
          </section>
        </div>
      </div>
    </div>
  );
}